// ── FollowUpsPage — the tickler queue and the rules that fill it ─────────────
// Two tabs on one page because they are one idea: the list you work, and the
// standing instructions that put things on it. Splitting them across two nav
// items would hide the rules from the person whose queue they fill.
//
// Everything here is scoped to the signed-in user by the API. A manager can
// read someone else's queue (the Owner picker) but cannot tick items off for
// them — completing a call you didn't make would poison the last-touch signal
// the cadences run on.
import React, { useState, useEffect } from "react";
import { MdSnooze } from "react-icons/md";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, FInput, FSelect, Field, FilterSelect, Modal, PageLink, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
// One vocabulary for the three kinds of touch, shared with the referral source
// timeline. Imported rather than restated so the queue and the record page can
// never label the same event differently.
import { SOURCE_META } from "../../../shared/SalesActivityPanel";

const TYPES = ["ReferralSource", "Lead", "Opportunity"];
const TYPE_LABEL = { ReferralSource: "Referral Source", Lead: "Lead", Opportunity: "Opportunity" };
const TABLE_OF = { ReferralSource: "ReferralSources", Lead: "Leads", Opportunity: "Opportunities" };
const VIEW_MODAL = { ReferralSource: "referralsource-view", Lead: "lead-view", Opportunity: "opp-view" };

// Record types that have a real PAGE to open. Referral sources do; leads and
// opportunities are modal-only in this app, so there is nothing to link them to
// and they keep the pop-up. When a Lead or Opportunity page exists, adding it
// here — and to SLIM_PAGES in App.js — is the whole change.
const PAGE_OF = { ReferralSource: "referral-source" };
const PAGE_PARAMS = {
  ReferralSource: (x) => ({ referralsourceid: x.entityid, companyname: x.entityname || "" }),
};

const BUCKETS = [
  { id: "overdue", label: "Overdue",   tone: { fg: "#993C1D", bg: "#FAECE7", br: "#E8B4A8" } },
  { id: "today",   label: "Today",     tone: { fg: "#7a5200", bg: "#FAEEDA", br: "#EBD3A0" } },
  { id: "week",    label: "This Week", tone: { fg: "#185FA5", bg: "#E6F1FB", br: "#BBD6F0" } },
  { id: "later",   label: "Later",     tone: { fg: "#5b5f66", bg: "#F1EFE8", br: "#DAD7CE" } },
];

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

// Where "Snooze 1w" will actually land, mirroring snoozeDate() on the server:
// a week from the due date if that is still ahead of us, otherwise a week from
// today. The old tooltip always said "today + 7", which was right for an overdue
// item and a lie for an already-snoozed one — and the snoozed list is made
// entirely of already-snoozed ones, so the lie would have been on show.
const snoozeTarget = (duedate, days) => {
  const base = duedate ? new Date(duedate + "T00:00:00Z").getTime() : 0;
  const from = base > Date.now() ? base : Date.now();
  return new Date(from + days * 86400000).toISOString().slice(0, 10);
};

// The "Last touch" cell: when, and what it was.
//
// The date alone was ambiguous in the way that matters — an email the system
// captured because the address happened to match reads identically to a call
// somebody actually made. Since the cadence resets on either, a rep needs to
// see which one bought them the silence before deciding the chase is wrong.
//
// Stacked rather than inline: the column is nowrap and dense, and widening it
// pushes the actions off the right edge on a laptop.
const TouchCell = ({ row }) => {
  const meta = SOURCE_META[row.lastsource];
  return <>
    {fmtDate(row.lasttouchutc)}
    {/* No chip when the view has not been migrated yet (lastsource undefined) —
        better a plain date than a box labelled "undefined". */}
    {meta && (
      <div style={{ marginTop: 2 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: meta.fg, background: meta.bg,
          border: "0.5px solid " + meta.br, borderRadius: 4, padding: "1px 5px",
          textTransform: "uppercase", whiteSpace: "nowrap",
        }}>{meta.label}</span>
      </div>
    )}
  </>;
};

export const FollowUpsPage = () => {
  const { api, role, currentUser, setModal, setConfirm: rawConfirm } = useApp();
  const setConfirm = (o) => o ? rawConfirm({ ...o, onConfirm: () => { rawConfirm(null); o.onConfirm(); } }) : rawConfirm(null);

  const [tab, setTab] = useState("queue");
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [status, setStatus] = useState("open");
  const [typeF, setTypeF] = useState("");
  const [owner, setOwner] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);     // ad-hoc add/edit
  const [completing, setCompleting] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    const params = { status };
    if (typeF) params.entitytype = typeF;
    if (owner) params.owner = owner;
    api.get("getFollowUps", params)
      .then(d => { setData(d && d.rows ? d : { rows: [], counts: {} }); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, typeF, owner]);

  useEffect(() => {
    if (!role.salesUnrestricted && !role.isAdmin) return;
    api.get("getUsers", { filter: "isbdo" }).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = (body, msg) => {
    setBusy(body.id); setNote(null);
    api.post("saveFollowUp", body)
      .then(() => { setBusy(null); setCompleting(null); setEditing(null); if (msg) setNote(msg); load(); })
      .catch(e => { setBusy(null); setErr(e.message); });
  };

  // Generation is on demand as well as scheduled: a rep who has just changed a
  // rule shouldn't wait until tomorrow to see what it does.
  const refresh = () => {
    setBusy("gen"); setNote(null);
    api.post("runFollowUpGeneration", {})
      .then(r => {
        setBusy(null);
        setNote(r.created ? `${r.created} new follow-up${r.created === 1 ? "" : "s"} added.`
                          : "Nothing new — everything on cadence is already on your list.");
        load();
      })
      .catch(e => { setBusy(null); setErr(e.message); });
  };

  // Leads and opportunities have no detail page, only modals, so the record is
  // fetched fresh rather than trusting a copy denormalised into the queue view.
  const openRecord = (x) => {
    api.get("getRecord", { table: TABLE_OF[x.entitytype], id: x.entityid })
      .then(rec => { if (rec) setModal({ type: VIEW_MODAL[x.entitytype], rec, accountid: rec.accountid, onDone: load }); })
      .catch(e => setErr(e.message));
  };

  if (loading) return <Spinner />;
  if (err && !data.rows.length) return <ErrorMsg msg={err} onRetry={load} />;

  const rows = data.rows || [];
  const counts = data.counts || {};
  const snoozedOpen = data.snoozedOpen || 0;
  // Snoozed items are OPEN items. Everything you can do to an open one — finish
  // it, push it again, edit it, drop it — has to work here too, or "where did my
  // snoozed items go" just becomes "why can't I do anything with them".
  const isOpenList = status === "open" || status === "snoozed";
  const canPickOwner = role.salesUnrestricted || role.isAdmin;

  return <>
    <SectionHeader title="Follow-Ups" actions={[
      <div key="tabs" style={{ display: "flex", gap: 4 }}>
        {[["queue", "My List"], ["rules", "Rules"]].map(([id, label]) => (
          <Btn key={id} variant={tab === id ? "primary" : "default"} onClick={() => setTab(id)}>{label}</Btn>
        ))}
      </div>,
      tab === "queue" ? <Btn key="add" variant="primary" onClick={() => setEditing({ duedate: today(), priority: "normal" })}>+ Follow-Up</Btn> : null,
      tab === "queue" ? <Btn key="gen" disabled={busy === "gen"} onClick={refresh}>{busy === "gen" ? "Checking…" : "Refresh from rules"}</Btn> : null,
    ]} />

    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 10 }}>{err}</div>}
    {note && (
      <div style={{ fontSize: 13, color: "#0F6E56", background: "#E1F5EE", border: "0.5px solid #B9E3D4",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{note}</div>
    )}
    {data.notInstalled && (
      <div style={{ fontSize: 13, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
        Follow-ups aren't installed yet — run <code>sql/2026-08-28_followups.sql</code>.
      </div>
    )}

    {tab === "rules"
      ? <RulesTab api={api} role={role} setConfirm={setConfirm} onChanged={load} />
      : <>
        {/* ── Filters ── */}
        <Card style={{ padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 14px" }}>
            {/* Status has no "all" option on purpose — open, done and dismissed
                are different lists, not degrees of one. "Snoozed" is not a
                fourth status: it is the open list narrowed to what you have
                already pushed once. It sits here because that is where you
                look, and it carries a count so a parked pile stays visible
                instead of quietly becoming a pile of forgotten. */}
            <FilterSelect label="Show" value={status} onChange={setStatus} allLabel={null}
              options={[
                { value: "open", label: "Open" },
                { value: "snoozed", label: snoozedOpen ? "Snoozed (" + snoozedOpen + ")" : "Snoozed" },
                { value: "done", label: "Done" },
                { value: "dismissed", label: "Dismissed" },
              ]} />
            <FilterSelect label="Type" value={typeF} onChange={setTypeF} allLabel="All types"
              options={TYPES.map(t => ({ value: t, label: TYPE_LABEL[t] }))} />
            {canPickOwner && users.length > 0 && (
              <FilterSelect label="Whose list" value={owner} onChange={setOwner}
                allLabel={"Mine (" + (currentUser.name || "me") + ")"}
                options={users.map(u => ({ value: u.id, label: u.FullName }))} />
            )}
            <span style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
              {BUCKETS.map(bk => counts[bk.id] ? (
                <span key={bk.id} style={{ fontSize: 12, fontWeight: 600, color: bk.tone.fg, background: bk.tone.bg,
                                           border: "0.5px solid " + bk.tone.br, borderRadius: 999, padding: "2px 10px" }}>
                  {bk.label} {counts[bk.id]}
                </span>
              ) : null)}
            </span>
          </div>
        </Card>

        {data.viewingOther && (
          <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                        borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
            You're looking at someone else's list. You can read it; only they can complete items on it.
          </div>
        )}

        {rows.length === 0 ? (
          <Card><div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 2px" }}>
            {status === "open"
              ? "Nothing due. Add a follow-up, or set up a rule on the Rules tab to have them raised for you."
              : status === "snoozed"
                ? "Nothing snoozed. Anything you push out with Snooze — or by editing its due date later — shows up here."
                : "Nothing here."}
          </div></Card>
        ) : BUCKETS.map(bk => {
          const inBucket = rows.filter(x => x.bucket === bk.id);
          if (!inBucket.length) return null;
          return (
            <Card key={bk.id} style={{ marginBottom: 12, overflowX: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                               color: bk.tone.fg, background: bk.tone.bg, border: "0.5px solid " + bk.tone.br,
                               borderRadius: 4, padding: "2px 8px" }}>{bk.label}</span>
                <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{inBucket.length}</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><Th>Due</Th><Th>Record</Th><Th>What</Th>{/* "Raised by", not "Source" — the Last touch column now carries a source
    chip of its own (note/email/follow-up) and two adjacent columns both
    called source, meaning different things, is a puzzle nobody needs. */}
<Th>Last touch</Th><Th>Raised by</Th><Th></Th></tr></thead>
                {/* Type sections inside the bucket, same shape as the section
                    headers on Leads and Accounts. Urgency stays the OUTER axis
                    — a tickler is about what has slipped, not about what kind
                    of thing slipped — so type groups within each bucket rather
                    than the other way round.

                    Headers are skipped when the bucket holds only one type:
                    a header exists to separate things, and there is nothing to
                    separate. That also keeps the view clean when the Type
                    filter is already narrowing to one. */}
                {/* Known types first, then anything unexpected — dropping a row
                    because its type isn't in the list would hide work, which is
                    the one thing a tickler must never do. */}
                {[...TYPES.filter(t => inBucket.some(x => x.entitytype === t)),
                  ...[...new Set(inBucket.map(x => x.entitytype))].filter(t => !TYPES.includes(t)).sort()
                ].map(t => {
                  const ofType = inBucket.filter(x => x.entitytype === t);
                  const showHeader = new Set(inBucket.map(x => x.entitytype)).size > 1;
                  return (
                    <tbody key={t}>
                      {showHeader && (
                        <tr>
                          <td colSpan={6} style={{ padding: "6px 12px", background: B.cyanLight }}>
                            <Badge s={TYPE_LABEL[t]} />
                            <span style={{ marginLeft: 8, color: B.cyanDark, fontSize: 11, fontWeight: 600 }}>
                              {ofType.length}
                            </span>
                          </td>
                        </tr>
                      )}
                      {ofType.map(x => (
                        <tr key={x.id}>
                          <Td style={{ whiteSpace: "nowrap" }}>
                            {fmtDate(x.duedate)}
                            {x.priority === "high" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#993C1D" }}>HIGH</span>}
                            {x.snoozedfrom && (
                              <div style={{ fontSize: 10, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 3 }}
                                title={"Originally due " + fmtDate(x.snoozedfrom)}>
                                <MdSnooze size={11} /> snoozed
                              </div>
                            )}
                          </Td>
                          <Td>
                            {/* Where a real page exists, this is a real anchor:
                                a new tab, so the queue you are working through
                                stays open behind it, and right-click / copy-link
                                / bookmark all behave the way they look like they
                                should. Types with no page keep the modal. */}
                            {PAGE_OF[x.entitytype] ? (
                              /* An ordinary PageLink. It carries a real href so
                                 right-click, copy-link and ctrl-click all behave
                                 natively, and its click goes through navClick —
                                 which is the single place that decides a record
                                 page opens in its own slim window. Putting that
                                 decision here instead would mean every list in
                                 the app needing its own copy of it. */
                              <PageLink page={PAGE_OF[x.entitytype]} params={PAGE_PARAMS[x.entitytype](x)}
                                style={{ fontWeight: 600, textDecoration: "underline", textDecorationStyle: "dotted" }}>
                                {x.entityname || x.entityid}
                              </PageLink>
                            ) : (
                              <span onClick={() => openRecord(x)} style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                                {x.entityname || x.entityid}
                              </span>
                            )}
                            <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                              {TYPE_LABEL[x.entitytype]}{x.entitystate ? " · " + x.entitystate : ""}
                            </div>
                          </Td>
                          <Td style={{ whiteSpace: "normal", maxWidth: 380 }}>
                            {x.title}
                            {x.details && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{x.details}</div>}
                          </Td>
                          {/* A bare date could be a real conversation or an
                              automated mail that happened to land in the right
                              mailbox. The chip says which, so a rep can judge
                              whether the chase the system raised is fair. */}
                          <Td style={{ whiteSpace: "nowrap", fontSize: 12, color: "var(--color-text-secondary)" }}>
                            {x.lasttouchutc ? <TouchCell row={x} /> : "never"}
                          </Td>
                          <Td style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                            {x.rulename || "By hand"}
                          </Td>
                          <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            {isOpenList && !data.viewingOther && <>
                              <Btn variant="primary" disabled={busy === x.id} onClick={() => setCompleting(x)}>Done</Btn>
                              {/* Only on the snoozed list, and only for something
                                  still parked in the future: the reason you go
                                  looking for a snoozed item is usually to pull it
                                  back. Moving a date EARLIER is not a deferral, so
                                  it stamps nothing and the "originally due" trail
                                  survives untouched. */}
                              {status === "snoozed" && x.bucket !== "overdue" && x.bucket !== "today" &&
                                <Btn disabled={busy === x.id}
                                  title="Bring this back to today's list"
                                  onClick={() => act({ action: "update", id: x.id, duedate: today() }, "Back on today's list.")}>
                                  Due now
                                </Btn>}
                              {/* "+1w" told you the effect but not the action. The
                                  snooze icon is the universal one for "not now,
                                  remind me later"; the word backs it up and the
                                  tooltip names the date it will land on. */}
                              <Btn disabled={busy === x.id}
                                title={"Snooze until " + fmtDate(snoozeTarget(x.duedate, 7))}
                                onClick={() => act({ action: "snooze", id: x.id, days: 7 }, "Snoozed for a week.")}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <MdSnooze size={14} color={B.cyan} /> Snooze 1w
                                </span>
                              </Btn>
                              <Btn disabled={busy === x.id} onClick={() => setEditing(x)}>Edit</Btn>
                              <Btn variant="danger" disabled={busy === x.id} onClick={() => setConfirm({
                                title: "Dismiss follow-up", confirmLabel: "Dismiss",
                                msg: x.ruleid
                                  ? "Drop this one? The rule will raise it again next time it's due — turn the rule off if you want it to stop for good."
                                  : "Drop this follow-up?",
                                onConfirm: () => act({ action: "dismiss", id: x.id }, "Dismissed."),
                              })}>Dismiss</Btn>
                            </>}
                            {!isOpenList && !data.viewingOther &&
                              <Btn disabled={busy === x.id} onClick={() => act({ action: "reopen", id: x.id }, "Back on your list.")}>Reopen</Btn>}
                          </div></Td>
                        </tr>
                      ))}
                    </tbody>
                  );
                })}
              </table>
            </Card>
          );
        })}
      </>}

    {editing && <FollowUpForm item={editing} onCancel={() => setEditing(null)} onSave={act} api={api} />}
    {completing && <CompleteForm item={completing} onCancel={() => setCompleting(null)} onSave={act} />}
  </>;
};

// ── Ad-hoc follow-up ─────────────────────────────────────────────────────────
// The "specific materials or questions" case. Details is free text on purpose:
// a rule can never know that you promised to bring the Q3 borrowing base.
const FollowUpForm = ({ item, onCancel, onSave, api }) => {
  const isNew = !item.id;
  const [f, setF] = useState({
    entitytype: item.entitytype || "Lead",
    entityid: item.entityid || "",
    title: item.title || "",
    details: item.details || "",
    duedate: (item.duedate || today()).slice(0, 10),
    priority: item.priority || "normal",
  });
  const [picks, setPicks] = useState([]);
  const [search, setSearch] = useState("");
  const set = k => v => setF(p => ({ ...p, [k]: v }));

  // Record picker, only when creating — an existing item's record is fixed.
  useEffect(() => {
    if (!isNew) return;
    const ep = f.entitytype === "Lead" ? "getLeads"
             : f.entitytype === "Opportunity" ? "getOpportunities" : "getReferralSources";
    api.get(ep).then(d => setPicks(Array.isArray(d) ? d : [])).catch(() => setPicks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.entitytype, isNew]);

  const labelOf = (r) => r.companyname || r.company_name || r.name
    || [r.firstname, r.lastname].filter(Boolean).join(" ") || String(r.id);
  const shown = picks
    .filter(r => !search || labelOf(r).toLowerCase().includes(search.toLowerCase()))
    .slice(0, 200);

  const valid = f.title.trim() && f.entityid && /^\d{4}-\d{2}-\d{2}$/.test(f.duedate);

  return (
    <Modal title={isNew ? "New follow-up" : "Edit follow-up"} onClose={onCancel}>
      {isNew && <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label="About"><FSelect value={f.entitytype} onChange={v => { set("entitytype")(v); set("entityid")(""); }}
            options={TYPES.map(t => ({ value: t, label: TYPE_LABEL[t] }))} /></Field>
          <Field label="Find"><FInput value={search} onChange={setSearch} placeholder="Type to narrow..." /></Field>
        </div>
        <Field label="Record">
          <FSelect value={f.entityid} onChange={set("entityid")}
            options={[{ value: "", label: shown.length ? "— Select —" : "Nothing to pick" },
                      ...shown.map(r => ({ value: String(r.id), label: labelOf(r) }))]} />
        </Field>
      </>}

      <Field label="What needs doing"><FInput value={f.title} onChange={set("title")} placeholder="Call Bob about the Q3 borrowing base" /></Field>
      <Field label="Details (materials, questions, context)">
        <textarea value={f.details} onChange={e => set("details")(e.target.value)} rows={4}
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, borderRadius: 4,
                   border: "1px solid #999", fontFamily: "inherit", resize: "vertical" }} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Due"><FInput value={f.duedate} onChange={set("duedate")} /></Field>
        <Field label="Priority"><FSelect value={f.priority} onChange={set("priority")}
          options={[{ value: "normal", label: "Normal" }, { value: "high", label: "High" }]} /></Field>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["Today", today()], ["Tomorrow", plusDays(1)], ["Next week", plusDays(7)], ["Next month", plusDays(30)]]
          .map(([label, d]) => <Btn key={label} onClick={() => set("duedate")(d)}>{label}</Btn>)}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" disabled={!valid}
          onClick={() => onSave(isNew ? { action: "create", ...f } : { action: "update", id: item.id, ...f },
                               isNew ? "Follow-up added." : "Updated.")}>
          {isNew ? "Add" : "Save"}
        </Btn>
      </div>
    </Modal>
  );
};

// Completing asks what happened. The outcome is the record of the call, and
// completing is itself a "touch" — so the next cadence counts from now.
const CompleteForm = ({ item, onCancel, onSave }) => {
  const [outcome, setOutcome] = useState("");
  return (
    <Modal title="Mark done" onClose={onCancel}>
      <div style={{ fontSize: 13, marginBottom: 4, fontWeight: 600 }}>{item.title}</div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        {item.entityname} · {TYPE_LABEL[item.entitytype]}
      </div>
      <Field label="What happened? (optional)">
        <textarea value={outcome} onChange={e => setOutcome(e.target.value)} rows={4} autoFocus
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, borderRadius: 4,
                   border: "1px solid #999", fontFamily: "inherit", resize: "vertical" }} />
      </Field>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        This counts as a touch, so any cadence on this record restarts from today.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" onClick={() => onSave({ action: "complete", id: item.id, outcome }, "Done.")}>Mark done</Btn>
      </div>
    </Modal>
  );
};

// Starting points, not policy. Deliberately wide nets with no filters — a new
// user should see the system work before learning to narrow it, and an empty
// filter set is the honest default for "everything of this type".
const STARTERS = [
  { name: "Referral sources — quarterly touch", entitytype: "ReferralSource", cadencedays: 90,
    why: "keeps every broker warm", itemtitle: "Quarterly check-in" },
  { name: "Top-rated sources — monthly",        entitytype: "ReferralSource", cadencedays: 30,
    why: "your best sources, more often", filters: { rating: ["A"] },
    itemtitle: "Monthly call with a top source" },
  { name: "New leads — chase in a week",        entitytype: "Lead", cadencedays: 7,
    why: "nothing new goes cold", filters: { status: ["New"] } },
  { name: "Working leads — fortnightly",        entitytype: "Lead", cadencedays: 14,
    why: "keeps live conversations moving", filters: { status: ["Working"] } },
  { name: "Open deals — fortnightly",           entitytype: "Opportunity", cadencedays: 14,
    why: "no deal sits untouched" },
];

// ── Rules ────────────────────────────────────────────────────────────────────
const RulesTab = ({ api, role, setConfirm, onChanged }) => {
  const [d, setD] = useState({ rules: [], templates: [], filterOptions: {}, filterFields: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [templating, setTemplating] = useState(null);   // rule being saved as a template
  const [note, setNote] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("getFollowUpRules")
      .then(x => { setD(x || {}); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const post = (body, msg) => api.post("saveFollowUpRule", body)
    .then(() => { setEditing(null); setNote(msg); load(); onChanged && onChanged(); })
    .catch(e => setErr(e.message));

  if (loading) return <Spinner />;

  const describe = (r) => {
    let filters = {};
    try { filters = JSON.parse(r.filters || "{}"); } catch (e) { filters = {}; }
    const parts = Object.entries(filters).map(([k, v]) => `${k.replace(/_/g, " ")}: ${[].concat(v).join(", ")}`);
    return parts.length ? parts.join(" · ") : "everything of this type";
  };

  return <>
    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 10 }}>{err}</div>}
    {note && <div style={{ fontSize: 13, color: "#0F6E56", background: "#E1F5EE", border: "0.5px solid #B9E3D4",
                           borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{note}</div>}

    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
      A rule is a standing instruction: <em>chase anything matching this that I haven't touched in N days</em>.
      Rules are yours alone — they only ever put items on your own list. A touch is a captured email, a note, or
      completing a follow-up.
    </div>

    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
      <Btn variant="primary" onClick={() => setEditing({ entitytype: "ReferralSource", cadencedays: 30, filters: {} })}>+ New Rule</Btn>
    </div>

    <Card style={{ marginBottom: 16, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><Th>Rule</Th><Th>Applies to</Th><Th>Every</Th><Th>Open</Th><Th>Last run</Th><Th></Th></tr></thead>
        <tbody>
          {d.rules.map(r => (
            <tr key={r.id} style={{ opacity: r.isactive ? 1 : 0.55 }}>
              <Td style={{ fontWeight: 500 }}>
                {r.name}
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{describe(r)}</div>
              </Td>
              <Td>{TYPE_LABEL[r.entitytype] || r.entitytype}</Td>
              <Td>{r.cadencedays} days</Td>
              <Td>{r.openitems || 0}</Td>
              <Td style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {r.lastrunat ? `${fmtDate(r.lastrunat)} · ${r.lastrungenerated || 0} raised` : "never"}
              </Td>
              <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <Btn onClick={() => setEditing({ ...r, filters: (() => { try { return JSON.parse(r.filters || "{}"); } catch (e) { return {}; } })() })}>Edit</Btn>
                <Btn onClick={() => post({ id: r.id, name: r.name, entitytype: r.entitytype, cadencedays: r.cadencedays,
                                           filters: (() => { try { return JSON.parse(r.filters || "{}"); } catch (e) { return {}; } })(),
                                           itemtitle: r.itemtitle, isactive: !r.isactive },
                                         r.isactive ? "Rule paused." : "Rule on.")}>
                  {r.isactive ? "Pause" : "Resume"}
                </Btn>
                <Btn onClick={() => setTemplating(r)}>Save as template</Btn>
                <Btn variant="danger" onClick={() => setConfirm({
                  title: "Delete rule", confirmLabel: "Delete",
                  msg: "Delete this rule? Any open items it raised are removed with it; completed ones stay as history.",
                  onConfirm: () => post({ action: "delete", id: r.id }, "Rule deleted."),
                })}>Delete</Btn>
              </div></Td>
            </tr>
          ))}
          {d.rules.length === 0 && (
            <tr><Td colSpan={6} style={{ color: "var(--color-text-secondary)" }}>
              No rules yet. Add one, or copy a template below.
            </Td></tr>
          )}
        </tbody>
      </table>
    </Card>

    {/* Two tiers, listed separately, because they mean different things:
        a DEFAULT is the firm's cadence; a PERSONAL one is your own shorthand
        that nobody else can see. Both sections render even when empty — hiding
        them meant a new user saw no trace the feature existed. */}
    <TemplateGroup
      title="Default templates"
      blurb={<>The firm's cadences — everyone sees these. Copying one makes it <em>yours</em> to edit or delete.</>}
      empty={d.canCreateDefaultTemplate
        ? <>None yet. Build a rule above, press <strong>Save as template</strong> and choose <strong>Default</strong>.
           It appears here for everyone; it never puts anything on anyone's list until they copy it.</>
        : <>None yet. An administrator can publish the firm's standard cadences here.</>}
      items={(d.templates || []).filter(t => t.isdefault)}
      onCopy={(t) => post({ action: "copy", templateid: t.id }, "Copied to your rules.")}
      onDelete={(t) => setConfirm({
        title: "Delete template", confirmLabel: "Delete",
        msg: "Delete this default template? Rules people already copied from it are unaffected.",
        onConfirm: () => post({ action: "delete", id: t.id }, "Template deleted."),
      })}
    />

    <TemplateGroup
      title="My templates"
      blurb={<>Your own saved starting points. <strong>Nobody else can see these</strong> — a personal template is
              as private as your referral sources.</>}
      empty={<>None yet. Press <strong>Save as template</strong> on any rule to keep it as a starting point you can
              re-use without it generating work.</>}
      items={(d.templates || []).filter(t => !t.isdefault && t.ismine)}
      onCopy={(t) => post({ action: "copy", templateid: t.id }, "Copied to your rules.")}
      onDelete={(t) => setConfirm({
        title: "Delete template", confirmLabel: "Delete",
        msg: "Delete this personal template?",
        onConfirm: () => post({ action: "delete", id: t.id }, "Template deleted."),
      })}
    />

    {/* Starters. Not database templates — just sensible cadences, one click from
        becoming your own rule. Solves the empty-system problem: a new BDO has
        somewhere to begin without anyone having published anything first. */}
    <Card>
      <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
        Suggested starters
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
        Common cadences to start from. Adding one creates a normal rule you own — change the days, narrow the
        filters, or delete it.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>{STARTERS.map((st, i) => {
          const have = d.rules.some(r => r.name === st.name);
          return (
            <tr key={i}>
              <Td style={{ fontWeight: 500 }}>{st.name}
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {TYPE_LABEL[st.entitytype]} · every {st.cadencedays} days · {st.why}
                </div>
              </Td>
              <Td style={{ textAlign: "right" }}>
                {have
                  ? <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Added</span>
                  : <Btn onClick={() => post({ name: st.name, entitytype: st.entitytype, cadencedays: st.cadencedays,
                                               filters: st.filters || {}, itemtitle: st.itemtitle },
                                             "Rule added — edit it to suit you.")}>Add</Btn>}
              </Td>
            </tr>
          );
        })}</tbody>
      </table>
    </Card>

    {templating && (
      <TemplateForm rule={templating} canDefault={!!d.canCreateDefaultTemplate}
        onCancel={() => setTemplating(null)}
        onSave={(body) => { setTemplating(null); post(body, body.scope === "default"
          ? "Saved as a default template — everyone can copy it."
          : "Saved as a personal template — only you can see it."); }} />
    )}

    {editing && (
      <RuleForm rule={editing} options={d.filterOptions || {}} fields={d.filterFields || {}}
        api={api} onCancel={() => setEditing(null)}
        onSave={(body) => post(body, editing.id ? "Rule saved." : "Rule created.")} />
    )}
  </>;
};

// One template list, rendered the same way for both tiers. Always shows,
// empty or not — the empty state is where people learn the feature exists.
const TemplateGroup = ({ title, blurb, empty, items, onCopy, onDelete }) => (
  <Card style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
      {title}
    </div>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>{blurb}</div>
    {items.length === 0
      ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{empty}</div>
      : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>{items.map(t => (
            <tr key={t.id}>
              <Td style={{ fontWeight: 500 }}>{t.name}
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {TYPE_LABEL[t.entitytype]} · every {t.cadencedays} days
                  {t.isdefault && !t.ismine ? ` · from ${t.authorname || "an administrator"}` : ""}
                </div>
              </Td>
              <Td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {t.alreadycopied
                  ? <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginRight: 8 }}>Copied</span>
                  : <Btn variant="primary" onClick={() => onCopy(t)}>Copy</Btn>}
                {/* You can delete a template you own — your own personal ones,
                    and (for an admin) the defaults you published. */}
                {t.ismine && <span style={{ marginLeft: 6 }}><Btn variant="danger" onClick={() => onDelete(t)}>Delete</Btn></span>}
              </Td>
            </tr>
          ))}</tbody>
        </table>
      )}
  </Card>
);

// Saving a rule as a template CLONES it — the rule you are saving keeps running.
// Flagging it in place would quietly switch off the cadence you rely on, since
// templates never generate.
const TemplateForm = ({ rule, canDefault, onCancel, onSave }) => {
  const [name, setName] = useState(rule.name || "");
  const [scope, setScope] = useState("personal");
  return (
    <Modal title="Save as template" onClose={onCancel}>
      <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
        Keeps <strong>{rule.name}</strong> as a starting point you can re-use. Your original rule carries on
        generating follow-ups exactly as it does now — a template never raises anything by itself.
      </div>

      <Field label="Template name"><FInput value={name} onChange={setName} /></Field>

      <Field label="Who can see it">
        <FSelect value={scope} onChange={setScope} options={[
          { value: "personal", label: "Personal — only me" },
          ...(canDefault ? [{ value: "default", label: "Default — everyone in the firm" }] : []),
        ]} />
      </Field>

      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 16 }}>
        {scope === "default"
          ? "Everyone sees this under Default templates. It still puts nothing on anyone's list until they copy it."
          : canDefault
            ? "Only you will see this."
            : "Only you will see this. Default templates — the firm's standard cadences — are created by an administrator."}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" disabled={!name.trim()}
          onClick={() => onSave({ action: "saveastemplate", id: rule.id, name: name.trim(), scope })}>Save</Btn>
      </div>
    </Modal>
  );
};

const RuleForm = ({ rule, options, fields, api, onCancel, onSave }) => {
  const [f, setF] = useState({
    name: rule.name || "",
    entitytype: rule.entitytype || "ReferralSource",
    cadencedays: rule.cadencedays || 30,
    itemtitle: rule.itemtitle || "",
    filters: rule.filters || {},
  });
  const [preview, setPreview] = useState(null);
  const set = k => v => setF(p => ({ ...p, [k]: v }));

  const avail = fields[f.entitytype] || [];
  const toggle = (field, value) => setF(p => {
    const cur = new Set([].concat(p.filters[field] || []));
    if (cur.has(value)) cur.delete(value); else cur.add(value);
    const next = { ...p.filters };
    if (cur.size) next[field] = [...cur]; else delete next[field];
    return { ...p, filters: next };
  });

  // Dry run against the real data before committing. A cadence that would raise
  // four hundred items is something you want to know now, not tomorrow morning.
  const runPreview = () => {
    setPreview({ loading: true });
    api.post("runFollowUpGeneration", { dryRun: true, ruleid: rule.id || undefined })
      .then(r => setPreview({ result: (r.results || [])[0] || { due: 0 } }))
      .catch(e => setPreview({ error: e.message }));
  };

  const valid = f.name.trim() && Number(f.cadencedays) >= 1;

  return (
    <Modal wide title={rule.id ? "Edit rule" : "New rule"} onClose={onCancel}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "0 16px" }}>
        <Field label="Name"><FInput value={f.name} onChange={set("name")} placeholder="A-rated Midwest brokers" /></Field>
        <Field label="Applies to"><FSelect value={f.entitytype} onChange={v => setF(p => ({ ...p, entitytype: v, filters: {} }))}
          options={TYPES.map(t => ({ value: t, label: TYPE_LABEL[t] }))} /></Field>
        <Field label="Chase after (days)"><FInput value={String(f.cadencedays)} onChange={v => set("cadencedays")(v.replace(/[^\d]/g, ""))} /></Field>
      </div>

      <Field label="Task wording (optional — leave blank for an automatic one)">
        <FInput value={f.itemtitle} onChange={set("itemtitle")} placeholder="Quarterly check-in call" />
      </Field>

      <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em",
                    marginTop: 14, marginBottom: 8, paddingBottom: 4, borderBottom: "1.5px solid " + B.cyanLight }}>
        Narrow it down
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        Pick nothing and the rule covers everything of this type — a wide net. Pick values to tighten it.
      </div>

      {avail.length === 0 && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12 }}>No filters available for this type.</div>}
      {avail.map(field => {
        const vals = ((options[f.entitytype] || {})[field]) || [];
        if (!vals.length) return null;
        const chosen = new Set([].concat(f.filters[field] || []));
        return (
          <div key={field} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
              {field.replace(/_/g, " ")}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {vals.map(v => (
                <button key={v} type="button" onClick={() => toggle(field, v)}
                  style={{ fontSize: 12, padding: "3px 10px", borderRadius: 999, cursor: "pointer",
                           border: "1px solid " + (chosen.has(v) ? B.cyan : "var(--color-border-secondary)"),
                           background: chosen.has(v) ? B.cyanLight : "transparent",
                           color: chosen.has(v) ? B.cyanDark : "var(--color-text-primary)",
                           fontWeight: chosen.has(v) ? 600 : 400 }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {preview && (
        <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, fontSize: 12,
                      background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)" }}>
          {preview.loading ? "Checking…"
            : preview.error ? <span style={{ color: "#993C1D" }}>{preview.error}</span>
            : <>
                <strong>{preview.result.due || 0}</strong> would be raised now
                {preview.result.candidates != null ? ` out of ${preview.result.candidates} record(s)` : ""}.
                {preview.result.skipped && (
                  <span style={{ color: "var(--color-text-secondary)" }}>
                    {" "}Skipped: {preview.result.skipped.tooRecent || 0} touched recently,
                    {" "}{preview.result.skipped.filtered || 0} filtered out,
                    {" "}{preview.result.skipped.alreadyOpen || 0} already on your list.
                  </span>
                )}
              </>}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        {rule.id && <Btn onClick={runPreview}>Preview</Btn>}
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" disabled={!valid}
          onClick={() => onSave({ id: rule.id, ...f, cadencedays: Number(f.cadencedays) })}>Save</Btn>
      </div>
    </Modal>
  );
};
